import { Injectable, inject } from "@angular/core";
import { maxBy, orderBy } from "lodash";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "~environments/environment";
import {
    ExternVirkeskopStatus,
    ExternVirkeskopViewModel,
    IExternVirkeskopSearchModel,
    ISearchExternVirkeskopDokumentResponse,
    ISearchExternVirkeskopResponse
} from "../models/externvirkeskop-models";

@Injectable({
    providedIn: "root"
})
export class ExternVirkeskopService {
    documentsReImported$ = new Subject<void>();
    searchResult$ = new BehaviorSubject<ExternVirkeskopViewModel[]>(null);
    selected$ = new BehaviorSubject<ExternVirkeskopViewModel[]>([]);

    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly http = inject(ApiClientService);

    download(): void {
        const selected = this.selected$.value;
        selected.forEach(d => {
            const dokument = this.getYoungestDokument(d);
            if (ObjectUtils.hasValue(dokument)) {
                window.open(`${environment.apiV2}/externvirkeskop/dokument/${dokument.id}/download`, "_blank");
            }
        });
    }

    getAllExternaOrganisationer(): Observable<string[]> {
        return this.http.get<string[]>("externvirkeskop/externaorganisationer", false);
    }

    getIcon(status: string): string {
        switch (status) {
            case ExternVirkeskopStatus.ok: {
                return "far fa-check-circle success";
            }
            case ExternVirkeskopStatus.ignorerad: {
                return "far fa-exclamation-square warning";
            }
            case ExternVirkeskopStatus.varning: {
                return "far fa-exclamation-triangle warning";
            }
            case ExternVirkeskopStatus.fel: {
                return "far fa-exclamation-triangle error";
            }
            case ExternVirkeskopStatus.fatal: {
                return "fa-solid fa-exclamation-triangle error";
            }
            default:
                return "far fa-exclamation-square";
        }
    }

    getLog(id: number): Observable<string> {
        return this.http.get<string>(`externvirkeskop/${id}/log`, false);
    }

    import(): void {
        const selected = this.selected$.value;
        selected.forEach(d => {
            const commandService = this.commandService.createInstance();
            commandService.addTransactionCommand({ $type: "ReImportVirkeskopskontraktCommand",
                virkeskopskontraktExternId: d.id });
            const obs = commandService.pushCommandQueueTransaction();
            this.busyService.addWork(obs)
                .subscribe(result => {
                    this.documentsReImported$.next();
                });
        });
    }

    searchForExternVirkeskopskontrakt(searchModel: IExternVirkeskopSearchModel): void {
        const model = {
            externBeteckning: searchModel.externBeteckning,
            leverantorer: searchModel.leverantorer?.map(x => x.namn),
            statusar: searchModel.statusar?.map(x => x.namn),
            datumMottagen: searchModel.datumMottagen,
            datumUppdaterad: searchModel.datumUppdaterad
        } as any;
        const params = ObjectUtils.removeUndefinedProperties(model, true);
        this.busyService.addWork(this.http.get<ISearchExternVirkeskopResponse[]>("externvirkeskop/", false, params)).subscribe(x => {
            this.searchResult$.next(orderBy(x, r => r.uppdateradDokumentdatum, "desc").map(z => new ExternVirkeskopViewModel(z)));
        });
    }

    private getYoungestDokument(extern: ExternVirkeskopViewModel): ISearchExternVirkeskopDokumentResponse | null {
        if (extern?.dokument?.length > 0) {
            return maxBy(extern.dokument, x => x.updated);
        }
        return null;
    }
}
